import React, { useState, useEffect, useCallback } from "react";
import { useSurvey } from "../context/SurveyContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import SurveyPreviewModal from "./SurveyPreviewModal";
import ShareSurveyModal from "./ShareSurveyModal";
import {
  Plus,
  FileText,
  Edit,
  Eye,
  BarChart3,
  Share2,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Clock,
  Calendar,
  Search,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Grid3X3,
  List,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import "./SurveyList.css";
import { getBackendUrl } from "../config/env";

const SurveyList: React.FC = () => {
  const { surveys: localSurveys, deleteSurvey } = useSurvey();
  const { user, centerInfo, selectedCenterId } = useAuth();
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewSurvey, setPreviewSurvey] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);

  const [shareSurvey, setShareSurvey] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [surveyResponses, setSurveyResponses] = useState<{
    [key: string]: number;
  }>({});

  // 필터링 상태
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [responseFilter, setResponseFilter] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(false);

  // 뷰 모드 상태
  const [viewMode, setViewMode] = useState<"card" | "table">("card");

  // 페이징 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(5); // 페이지당 5개 항목
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // 각 설문조사의 응답 수를 가져오는 함수
  const fetchResponseCounts = useCallback(async (surveyList: any[]) => {
    const counts: { [key: string]: number } = {};
    const centerUserId =
      selectedCenterId || user?.centerId || centerInfo?.centerId;

    for (const survey of surveyList) {
      try {
        const surveyId = survey.id || survey._id;
        const url = centerUserId
          ? `${getBackendUrl()}/api/responses/survey/${surveyId}?centerUserId=${centerUserId}`
          : `${getBackendUrl()}/api/responses/survey/${surveyId}`;

        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          // 페이징 응답과 비-페이징 응답 모두 처리
          if (data.data && typeof data.total === "number") {
            // 페이징 응답
            counts[surveyId] = data.total;
          } else if (Array.isArray(data)) {
            // 비-페이징 응답 (기존 형식)
            counts[surveyId] = data.length;
          } else {
            counts[surveyId] = 0;
          }
        }
      } catch (error) {
        console.error(
          `Failed to fetch response count for survey ${survey.id}:`,
          error
        );
        counts[survey.id || survey._id] = 0;
      }
    }

    setSurveyResponses(counts);
  }, [selectedCenterId, user?.centerId, centerInfo?.centerId]);

  const fetchSurveys = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const centerUserId =
        selectedCenterId || user?.centerId || centerInfo?.centerId;
      
      // URL 파라미터 구성
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
      });

      // 센터 ID 추가
      if (centerUserId) {
        params.append("centerUserId", centerUserId);
      }

      // 필터 파라미터 추가
      if (searchTerm.trim()) {
        params.append("search", searchTerm.trim());
      }
      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      if (responseFilter !== "all") {
        params.append("hasResponses", responseFilter === "with_responses" ? "true" : "false");
      }

      const surveysUrl = `${getBackendUrl()}/api/surveys?${params.toString()}`;

      const response = await fetch(surveysUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("설문조사 목록을 불러오는데 실패했습니다");
      }

      const data = await response.json();
      
      // 페이징 응답 처리
      if (data.data && typeof data.total === "number") {
        setSurveys(data.data);
        setTotalItems(data.total);
        setTotalPages(data.totalPages);
        // 응답 수 가져오기
        await fetchResponseCounts(data.data);
      } else {
        // 기존 형식 (페이징 미지원) 호환성
        setSurveys(data);
        setTotalItems(data.length);
        setTotalPages(Math.ceil(data.length / pageSize));
        await fetchResponseCounts(data);
      }
    } catch (err) {
      console.error("Error fetching surveys:", err);
      setError(
        err instanceof Error
          ? err.message
          : "설문조사 목록을 불러오는데 실패했습니다"
      );
      // 오류 발생 시 로컬 데이터 사용
      setSurveys(localSurveys);
      setTotalItems(localSurveys.length);
      setTotalPages(Math.ceil(localSurveys.length / pageSize));
    } finally {
      setLoading(false);
    }
  }, [selectedCenterId, currentPage, searchTerm, statusFilter, responseFilter, pageSize, localSurveys, user?.centerId, centerInfo?.centerId, fetchResponseCounts]);

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]); // 필터와 페이지 변경 시 재조회

  // 필터 변경 시 첫 페이지로 리셋
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, responseFilter]);

  const handleDelete = async (surveyId: string) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) {
      return;
    }

    try {
      const response = await fetch(
        `${getBackendUrl()}/api/surveys/${surveyId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("설문조사 삭제에 실패했습니다");
      }

      // 로컬 상태 업데이트
      deleteSurvey(surveyId);
      // 목록 새로고침
      fetchSurveys();
    } catch (err) {
      console.error("Error deleting survey:", err);
      alert(
        err instanceof Error ? err.message : "설문조사 삭제에 실패했습니다"
      );
    }
  };

  const handleShare = (survey: any) => {
    setShareSurvey({
      id: survey.id || survey._id,
      title: survey.title,
    });
  };

  // 필터 초기화
  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setResponseFilter("all");
    setCurrentPage(1);
  };

  // 활성 필터 개수
  const activeFiltersCount = [
    searchTerm,
    statusFilter !== "all" ? statusFilter : null,
    responseFilter !== "all" ? responseFilter : null,
  ].filter(Boolean).length;

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // 페이징 컴포넌트
  const PaginationComponent = () => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;
      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      return pages;
    };

    return (
      <div className="flex items-center justify-between mt-6">
        <div className="text-sm text-gray-600">
          총 {totalItems}개 중 {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalItems)}개 표시
        </div>
        <div className="flex items-center gap-2">
          {/* 첫 페이지 */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className="h-8 w-8 p-0"
          >
            <ChevronsLeft size={16} />
          </Button>
          
          {/* 이전 페이지 */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft size={16} />
          </Button>

          {/* 페이지 번호들 */}
          {getPageNumbers().map((page) => (
            <Button
              key={page}
              variant={page === currentPage ? "default" : "outline"}
              size="sm"
              onClick={() => handlePageChange(page)}
              className="h-8 min-w-8 px-3"
            >
              {page}
            </Button>
          ))}

          {/* 다음 페이지 */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="h-8 w-8 p-0"
          >
            <ChevronRight size={16} />
          </Button>

          {/* 마지막 페이지 */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="h-8 w-8 p-0"
          >
            <ChevronsRight size={16} />
          </Button>
        </div>
      </div>
    );
  };

  const handlePreview = async (surveyId: string) => {
    try {
      const response = await fetch(
        `${getBackendUrl()}/api/surveys/${surveyId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("설문조사를 불러오는데 실패했습니다");
      }

      const data = await response.json();
      setPreviewSurvey(data);
      setShowPreview(true);
    } catch (err) {
      console.error("Error loading survey for preview:", err);
      // 로컬 데이터로 폴백
      const localSurvey = surveys.find((s) => (s.id || s._id) === surveyId);
      if (localSurvey) {
        setPreviewSurvey(localSurvey);
        setShowPreview(true);
      } else {
        alert("설문조사를 불러올 수 없습니다");
      }
    }
  };

  return (
    <div className="survey-list-container">
      <div className="survey-list-header">
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <FileText size={32} />내 설문조사
          </h1>
          <p className="page-description">
            생성한 설문조사를 관리하고 응답을 확인하세요
          </p>
        </div>
        <Button
          onClick={() => navigate("/create")}
          className="flex items-center gap-2"
        >
          <Plus size={20} />새 설문조사 만들기
        </Button>
      </div>

      {/* 필터링 섹션 */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <Input
              placeholder="제목으로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          {/* 뷰 모드 전환 버튼 */}
          <div className="flex items-center border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === "card" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("card")}
              className="rounded-none border-0"
              title="카드 뷰"
            >
              <Grid3X3 size={16} />
            </Button>
            <Button
              variant={viewMode === "table" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("table")}
              className="rounded-none border-0"
              title="테이블 뷰"
            >
              <List size={16} />
            </Button>
          </div>
          
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter size={18} />
            필터
            {activeFiltersCount > 0 && (
              <Badge variant="secondary" className="ml-1">
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
          {activeFiltersCount > 0 && (
            <Button
              variant="ghost"
              onClick={resetFilters}
              className="flex items-center gap-2 text-gray-500"
            >
              <X size={16} />
              초기화
            </Button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div>
              <label className="block text-sm font-medium mb-2">게시 상태</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="게시 상태 선택" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">전체</SelectItem>
                  <SelectItem value="published">게시됨</SelectItem>
                  <SelectItem value="draft">초안</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">응답 여부</label>
              <Select value={responseFilter} onValueChange={setResponseFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="응답 여부 선택" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">전체</SelectItem>
                  <SelectItem value="with_responses">응답 있음</SelectItem>
                  <SelectItem value="no_responses">응답 없음</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* 필터링 결과 표시 */}
        <div className="flex items-center justify-between text-sm text-gray-600 mt-4">
          <span>
            총 {totalItems}개 설문조사
          </span>
          {activeFiltersCount > 0 && (
            <div className="flex items-center gap-2">
              <span>활성 필터:</span>
              {searchTerm && (
                <Badge variant="outline" className="flex items-center gap-1">
                  검색: "{searchTerm}"
                  <X
                    size={12}
                    className="cursor-pointer"
                    onClick={() => setSearchTerm("")}
                  />
                </Badge>
              )}
              {statusFilter !== "all" && (
                <Badge variant="outline" className="flex items-center gap-1">
                  상태: {statusFilter === "published" ? "게시됨" : "초안"}
                  <X
                    size={12}
                    className="cursor-pointer"
                    onClick={() => setStatusFilter("all")}
                  />
                </Badge>
              )}
              {responseFilter !== "all" && (
                <Badge variant="outline" className="flex items-center gap-1">
                  응답:{" "}
                  {responseFilter === "with_responses" ? "있음" : "없음"}
                  <X
                    size={12}
                    className="cursor-pointer"
                    onClick={() => setResponseFilter("all")}
                  />
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <p>설문조사를 불러오는 중...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchSurveys}>
            다시 시도
          </button>
        </div>
      ) : surveys.length === 0 ? (
        <div className="text-center py-16">
          <FileText size={64} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            설문조사가 없습니다
          </h3>
          <p className="text-gray-500 mb-6">
            설문조사를 만들어 시작해보세요
          </p>

        </div>
      ) : (
        <>
          {/* 카드 뷰 */}
          {viewMode === "card" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {surveys.map((survey) => {
            const surveyId = survey.id || survey._id;
            const responseCount = surveyResponses[surveyId] || 0;
            const hasResponses = responseCount > 0;
            const isPublished =
              survey.status === "published" || survey.isPublished;

            return (
              <Card
                key={surveyId}
                className="hover:shadow-lg transition-shadow duration-200 flex flex-col h-full"
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg font-semibold truncate pr-2">
                        {survey.title}
                      </CardTitle>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                        {survey.description || "설명이 없습니다."}
                      </p>
                    </div>
                    <Badge
                      variant={isPublished ? "default" : "secondary"}
                      className={`ml-2 flex items-center gap-1 ${
                        isPublished
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {isPublished ? (
                        <CheckCircle size={12} />
                      ) : (
                        <Clock size={12} />
                      )}
                      {isPublished ? "게시됨" : "초안"}
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent className="pt-0 flex flex-col flex-1">
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                    <div className="flex items-center gap-1">
                      <FileText size={14} />
                      <span>{survey.questions?.length || 0} 문항</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <BarChart3 size={14} />
                      <span>{responseCount} 응답</span>
                    </div>
                  </div>

                  {/* 게시 기간 표시 */}
                  {(survey.publishStartDate || survey.publishEndDate) && (
                    <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-950 rounded-lg">
                      <div className="flex items-center gap-1 text-sm text-blue-700 dark:text-blue-300">
                        <Calendar size={14} />
                        <span className="font-medium">게시 기간:</span>
                        <span>
                          {survey.publishStartDate
                            ? new Date(
                                survey.publishStartDate
                              ).toLocaleDateString("ko-KR")
                            : "즉시"}
                          {" ~ "}
                          {survey.publishEndDate
                            ? new Date(
                                survey.publishEndDate
                              ).toLocaleDateString("ko-KR")
                            : "무기한"}
                        </span>
                      </div>
                      {/* 게시 상태 표시 */}
                      {(() => {
                        const now = new Date();
                        const startDate = survey.publishStartDate
                          ? new Date(survey.publishStartDate)
                          : null;
                        const endDate = survey.publishEndDate
                          ? new Date(survey.publishEndDate)
                          : null;

                        if (startDate && now < startDate) {
                          return (
                            <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                              ⏳{" "}
                              {Math.ceil(
                                (startDate.getTime() - now.getTime()) /
                                  (1000 * 60 * 60 * 24)
                              )}
                              일 후 게시 예정
                            </div>
                          );
                        } else if (endDate && now > endDate) {
                          return (
                            <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                              ❌ 게시 기간 종료
                            </div>
                          );
                        } else if (isPublished) {
                          return (
                            <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                              ✅ 현재 게시 중
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  )}

                  {/* 경고 메시지 영역 - 고정 높이 */}
                  <div
                    className="mb-4"
                    style={{
                      minHeight: hasResponses && isPublished ? "auto" : "0",
                    }}
                  >
                    {hasResponses && isPublished && (
                      <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <AlertTriangle
                          size={16}
                          className="text-yellow-600 flex-shrink-0"
                        />
                        <span className="text-sm text-yellow-800">
                          응답이 있는 설문조사는 수정할 수 없습니다.
                        </span>
                      </div>
                    )}
                  </div>

                  {/* 버튼 영역 - 하단에 고정 */}
                  <div className="mt-auto">
                    <div className="flex items-center justify-between gap-2 pt-2">
                      <div className="flex gap-2 flex-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/edit/${surveyId}`)}
                          disabled={hasResponses && isPublished}
                          className="flex items-center gap-1"
                        >
                          <Edit size={14} />
                          편집
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePreview(surveyId)}
                          className="flex items-center gap-1"
                        >
                          <Eye size={14} />
                          미리보기
                        </Button>
                      </div>
                      <div className="flex gap-1">
                        {isPublished && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleShare(survey)}
                            title="공유"
                            className="p-2"
                          >
                            <Share2 size={16} />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/analytics/${surveyId}`)}
                          title="결과 분석"
                          className="p-2"
                        >
                          <BarChart3 size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(surveyId)}
                          title="삭제"
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
              })}
            </div>
          ) : (
            /* 테이블 뷰 */
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>제목</TableHead>
                    <TableHead>상태</TableHead>
                    <TableHead>문항수</TableHead>
                    <TableHead>응답수</TableHead>
                    <TableHead>생성일</TableHead>
                    <TableHead className="text-right">작업</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {surveys.map((survey) => {
                    const surveyId = survey.id || survey._id;
                    const responseCount = surveyResponses[surveyId] || 0;
                    const hasResponses = responseCount > 0;
                    const isPublished =
                      survey.status === "published" || survey.isPublished;

                    return (
                      <TableRow key={surveyId}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{survey.title}</div>
                            <div className="text-sm text-gray-600 truncate max-w-xs">
                              {survey.description || "설명이 없습니다."}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={isPublished ? "default" : "secondary"}
                            className={`flex items-center gap-1 w-fit ${
                              isPublished
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {isPublished ? (
                              <CheckCircle size={12} />
                            ) : (
                              <Clock size={12} />
                            )}
                            {isPublished ? "게시됨" : "초안"}
                          </Badge>
                        </TableCell>
                        <TableCell>{survey.questions?.length || 0}</TableCell>
                        <TableCell>{responseCount}</TableCell>
                        <TableCell>
                          {survey.createdAt
                            ? new Date(survey.createdAt).toLocaleDateString("ko-KR")
                            : "알 수 없음"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/edit/${surveyId}`)}
                              disabled={hasResponses && isPublished}
                              title="편집"
                              className="p-2"
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePreview(surveyId)}
                              title="미리보기"
                              className="p-2"
                            >
                              <Eye size={16} />
                            </Button>
                            {isPublished && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleShare(survey)}
                                title="공유"
                                className="p-2"
                              >
                                <Share2 size={16} />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/analytics/${surveyId}`)}
                              title="결과 분석"
                              className="p-2"
                            >
                              <BarChart3 size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(surveyId)}
                              title="삭제"
                              className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}

      {/* 페이징 */}
      {!loading && !error && surveys.length > 0 && <PaginationComponent />}

      {showPreview && previewSurvey && (
        <SurveyPreviewModal
          isOpen={showPreview}
          onClose={() => {
            setShowPreview(false);
            setPreviewSurvey(null);
          }}
          survey={{
            title: previewSurvey.title || "",
            description: previewSurvey.description || "",
            questions: previewSurvey.questions || [],
          }}
          isTemplate={false}
        />
      )}

      {shareSurvey && (
        <ShareSurveyModal
          isOpen={!!shareSurvey}
          onClose={() => setShareSurvey(null)}
          surveyId={shareSurvey.id}
          surveyTitle={shareSurvey.title}
        />
      )}
    </div>
  );
};

export default SurveyList;
